import { isNull, isEmpty, isBoolean, uniq, omit } from "lodash";
import { getFootageValue, singleValue, orderStatus } from "../../../utils";
import pogDataUpdater from "./pogDataUpdater";

export const prepareInitialData = (data = []) => {
  const updatedData = [];
  if (data.length) {
    for (let i = 0; i < data.length; i++) {
      const pog = data[i];
      updatedData.push({
        ...pog,
        selection: false,
        virtualLoading: true,
        PlanogramName: pog?.PlanogramName?.trim(),
        prevSelected: pog?.Selected,
        AdjLineal: isNull(pog?.AdjLineal)
          ? pog?.LinealFootage
            ? String(getFootageValue(pog?.LinealFootage, true)).trim()
            : getFootageValue(pog?.LinealFootage, true)
          : getFootageValue(pog?.AdjLineal, true),
        __original: {
          ...pog,
          selection: false,
          virtualLoading: true,
        },
      });
    }
    return updatedData;
  }
  return data;
};

export const updateDuplicatePogNames = ({
  field,
  value,
  id,
  PogName,
  setSelectedPogNames,
  isPogSelected,
  isPogChecked,
  originalData,
}) => {
  if (field === "selection" || field === "Selected") {
    let originalPog = {};
    // To prevent selected Y pog marked as duplicate.
    for (let h = 0, len = originalData.length; h < len; h++) {
      const _pog = originalData[h] || {};
      if (_pog?.PlanogramNumber === id) {
        originalPog = { ..._pog };
        break;
      }
    }
    if (originalPog?.Selected !== singleValue.yes) {
      if ((isBoolean(value) && value) || [singleValue.yes].includes(value)) {
        setSelectedPogNames((oldList) => uniq([...oldList, PogName]));
      } else {
        if (
          (field === "selection" &&
            !value &&
            [singleValue.no].includes(isPogSelected)) ||
          (field === "selection" && !value && !isPogChecked) ||
          (field === "Selected" &&
            [singleValue.no].includes(value) &&
            !isPogChecked)
        ) {
          setSelectedPogNames((oldList) =>
            oldList.filter((name) => name !== PogName)
          );
        }
      }
    }
  }
};

export const resetPogFieldsOnSelection = (field, pog, originalAdjLf) => {
  if (field === "Selected") {
    let currentPog = { ...pog };
    // If selection is made from value 'Y' to 'E'/'N' then reset all values.
    if (pog[field] !== singleValue.yes) {
      currentPog = {
        ...currentPog,
        DatingDays: 0,
        AdjCost: 0,
        AdjQty: 0,
        AcqCost: 0,
        SelectedItems: 0,
        ReleaseStatus: "",
        ReleaseDate: null,
        PONumber: "",
      };
    }
    return currentPog;
  }
  return pog;
};

export const checkPogsBeforeSave = (
  dispatch,
  showNotifier,
  isSelected = true,
  pogList = [],
  selections = [],
  originalList = [],
  formData = null,
  isOtherAction = false
) => {
  let isValidSaveData = true;
  // If no 'Selected' field value preset in settings form.
  if (!isSelected) {
    for (let i = 0, len = pogList.length; i < len; i++) {
      const pog = pogList[i] || {};
      if (selections.includes(pog?.PlanogramNumber)) {
        const originalPog = originalList?.find(
          (i) => i?.PlanogramNumber === pog?.PlanogramNumber
        );
        let isValidDiff = true;
        if (
          isOtherAction &&
          ([singleValue.no].includes(originalPog?.Selected) ||
            [singleValue.no].includes(pog?.Selected))
        ) {
          isValidDiff = false;
        }
        if (!isEmpty(formData?.selected)) {
          if (formData?.selected !== originalPog.Selected) {
            isValidDiff = true;
          }
        }
        if (!isValidDiff) {
          isValidSaveData = false;
          dispatch(
            showNotifier({
              message:
                "Few checked POGs are not saved or marked as selected 'N'. Please verify 'Selected' field.",
              severity: "error",
            })
          );
          break;
        }
      }
    }
  }
  return isValidSaveData;
};

export const prepareSettingsSaveData = (
  pogFilters,
  details,
  storeId,
  userId,
  pogs = [],
  selections = []
) => {
  let updatedDetails = {
    storeId,
    userId,
    skuNumber: pogFilters?._sku,
    vendorName: pogFilters?._vendor,
    pogList: [],
  };
  if (pogs.length && selections.length) {
    for (let i = 0; i < pogs.length; i++) {
      const pog = pogs[i] || {};
      if (selections.includes(pog?.PlanogramNumber)) {
        updatedDetails = pogDataUpdater(updatedDetails, pog, details);
        if (selections.length === updatedDetails.pogList.length) {
          break;
        }
      }
    }
  }
  return updatedDetails;
};

export const postSettingsSaveUpdateData = (
  formData,
  pogs,
  updatedPogDetails,
  originalData,
  selections = []
) => {
  const updatedPogs = [];
  for (let i = 0; i < pogs.length; i++) {
    let pog = pogs[i] || {};
    if (selections?.includes(pog?.PlanogramNumber)) {
      if (!isEmpty(formData?.selected)) {
        pog = { ...pog, Selected: formData?.selected };
        pog = {
          ...resetPogFieldsOnSelection("Selected", pog, pog?.LinealFootage),
        };
      }

      if ([formData?.selected, pog?.Selected].includes(singleValue.yes)) {
        const originalPog = originalData?.find(
          (p) => p?.PlanogramNumber === pog?.PlanogramNumber
        );
        if ([singleValue.no].includes(originalPog?.Selected)) {
          pog = {
            ...pog,
            ReleaseStatus: orderStatus.notReleased,
          };
        }
      }

      // Update pog summary fetched from server end.
      const pogDetails = updatedPogDetails.find(
        (p) => p.PogNumber === pog.PlanogramNumber
      );

      const selectionValue = !!formData?.selected
        ? formData?.selected
        : pog?.Selected;

      pog = {
        ...pog,
        Selected: selectionValue,
        prevSelected: selectionValue,
        AdjCost: pogDetails?.AdjCost,
        AdjQty: pogDetails?.AdjQty,
        AcqCost: pogDetails?.AcqCost,
        SelectedItems: pogDetails?.SelectedItems,
        __original: {
          ...pog,
          prevSelected: selectionValue,
          AdjCost: pogDetails?.AdjCost,
          AdjQty: pogDetails?.AdjQty,
          AcqCost: pogDetails?.AcqCost,
          SelectedItems: pogDetails?.SelectedItems,
          Selected: selectionValue,
          selection: false,
          virtualLoading: true,
        },
      };
      updatedPogs.push(pog);
    }
  }
  return updatedPogs;
};

export const saveUpdatedPogs =
  (postSaveUpdatedPogs, isCreatePO = false) =>
  (oldPogDetails) => {
    let _updates = [];
    for (let i = 0, len = oldPogDetails?.length; i < len; i++) {
      const pog = oldPogDetails[i] || {};
      const updatedPogDetails = postSaveUpdatedPogs?.find(
        (updatedPog) => updatedPog?.PlanogramNumber === pog?.PlanogramNumber
      );
      if (updatedPogDetails) {
        _updates.push({
          ...updatedPogDetails,
          selection: isCreatePO ? updatedPogDetails.selection : false, // removing selections after saving data except create PO.
        });
      } else {
        _updates.push(pog);
      }
    }
    return _updates;
  };

export const prepareInlineSaveData = (pogFilters, pog, storeId, userId) => {
  const noSelected = pog?.Selected === singleValue.no;
  return {
    storeId,
    userId,
    skuNumber: pogFilters?._sku,
    vendorName: pogFilters?._vendor,
    pogList: [
      {
        storePlanogramId: pog?.PlanogramNumber,
        selected: pog.Selected,
        departmentLetter: pog?.DepartmentLetter,
        releaseStatus: noSelected ? "" : pog?.ReleaseStatus,
        releaseDate: noSelected ? null : pog?.ReleaseDate,
        poNumber: noSelected ? "" : pog?.PONumber,
      },
    ],
  };
};

export const postInlineSaveUpdateData = (pog, pogs, summaryList) => {
  const updatedPogs = [];
  for (let i = 0, len = pogs.length; i < len; i++) {
    let updatedPog = pogs[i] || {};
    if (updatedPog?.PlanogramNumber === pog?.PlanogramNumber) {
      const _updatedPog =
        summaryList?.find((_pog) => _pog?.PogNumber === pog?.PlanogramNumber) ||
        {};
      let releaseStatus =
        pog?.Selected === singleValue.no
          ? ""
          : !!pog?.ReleaseStatus
          ? pog?.ReleaseStatus
          : orderStatus.notReleased;
      updatedPog = {
        ...pog,
        ...omit(_updatedPog, "Cost"),
        ReleaseStatus: releaseStatus,
        __original: {
          ...omit(_updatedPog, "Cost"),
          Selected: _updatedPog?.Selected,
          selection: false,
          virtualLoading: true,
        },
      };
    }
    updatedPogs.push(updatedPog);
  }
  return updatedPogs;
};

export const saveUpdatedInlinePogs =
  (postSaveUpdatedPogs) => (oldPogDetails) => {
    let _updates = [];
    for (let i = 0, len = oldPogDetails?.length; i < len; i++) {
      const pog = oldPogDetails[i] || {};
      const updatedPogDetails = postSaveUpdatedPogs?.find(
        (updatedPog) => updatedPog?.PlanogramNumber === pog?.PlanogramNumber
      );
      if (updatedPogDetails) {
        _updates.push({
          ...updatedPogDetails,
          selection: false, // removing selections after saving data.
          __original: {
            ...updatedPogDetails,
            Selected: updatedPogDetails?.Selected,
            selection: false,
            virtualLoading: true,
          },
        });
      } else {
        _updates.push(pog);
      }
    }
    return _updates;
  };

export const prepareCreatePOData = (
  pogFilters,
  filteredData,
  storeId,
  formData,
  userId,
  selections = []
) => {
  let preparedPogs = {};
  if (selections.length) {
    const selectedPogs =
      filteredData?.filter((_p) => selections?.includes(_p?.PlanogramNumber)) ||
      [];

    const pogDetails = selectedPogs?.map((_p) => {
      return {
        storePlanogramId: _p?.PlanogramNumber,
        departmentLetter: _p?.DepartmentLetter,
      };
    });
    preparedPogs = {
      storeId,
      poNumber: formData?.customPONumber, // Mandatory
      createPOBy:
        formData?.generatePOBy === "Department"
          ? "DEPT"
          : formData?.generatePOBy, // Mandatory
      userId: userId, // Mandatory
      pogList: pogDetails,
      skuNumber: pogFilters?._sku,
      vendorName: pogFilters?._vendor,
    };
  }
  return preparedPogs;
};

export const postCreatePOUpdateData = (pogs, details = [], selections = []) => {
  const preparedPogList = [];
  if (selections.length && details.length) {
    for (let i = 0, len = pogs.length; i < len; i++) {
      let pog = pogs[i] || {};
      if (selections.includes(pog?.PlanogramNumber)) {
        const pogWithPoNumber = details.find(
          (i) => i?.PogNumber === pog?.PlanogramNumber
        );
        preparedPogList.push({
          ...pog,
          PONumber: pogWithPoNumber?.PONumber,
        });
      }
    }
  }
  return preparedPogList;
};

export const prepareSaveDatesData = (
  pogFilters,
  filteredData,
  date,
  storeId,
  userDetails,
  selections = []
) => {
  let updatedData = {};
  if (selections.length) {
    const selectedPogs =
      filteredData?.filter((_p) => selections?.includes(_p?.PlanogramNumber)) ||
      [];

    const pogDetails = selectedPogs?.map((_p) => {
      return {
        storePlanogramId: _p?.PlanogramNumber,
        departmentLetter: _p?.DepartmentLetter,
      };
    });
    updatedData = {
      storeId,
      releaseDate: date,
      userId: userDetails?.email,
      skuNumber: pogFilters?._sku,
      vendorName: pogFilters?._vendor,
      pogList: pogDetails,
    };
  }
  return updatedData;
};

export const postSaveDatesUpdateData = (
  date,
  pogs,
  selections = [],
  responsePogs = []
) => {
  const updatedPogs = [];
  for (let i = 0; i < pogs.length; i++) {
    let pog = pogs[i] || {};
    const _pog = responsePogs?.find(
      (p) => p?.PogNumber === pog?.PlanogramNumber
    );
    if (selections?.includes(pog?.PlanogramNumber)) {
      if (
        date !== "" &&
        pog.Selected === singleValue.yes &&
        _pog?.CountOfPOItems === 0 // If all items having same date then only show in POG UI then API will give 0 count.
      ) {
        pog = { ...pog, ReleaseDate: _pog?.ReleaseDate };
      }

      updatedPogs.push(pog);
    }
  }
  return updatedPogs;
};

export const prepareDeleteDatesData = (
  pogFilters,
  filteredData,
  storeId,
  userEmail,
  selections = []
) => {
  let updatedData = {};
  if (selections.length) {
    const selectedPogs =
      filteredData?.filter((_p) => selections?.includes(_p?.PlanogramNumber)) ||
      [];

    const pogDetails = selectedPogs?.map((_p) => {
      return {
        storePlanogramId: _p?.PlanogramNumber,
        departmentLetter: _p?.DepartmentLetter,
      };
    });
    updatedData = {
      storeId,
      userId: userEmail,
      skuNumber: pogFilters?._sku,
      vendorName: pogFilters?._vendor,
      pogList: pogDetails,
    };
  }
  return updatedData;
};

export const postDeleteDatesUpdateData = (pogs, selections = []) => {
  const updatedPogs = [];
  for (let i = 0; i < pogs.length; i++) {
    let pog = pogs[i] || {};
    if (selections?.includes(pog?.PlanogramNumber)) {
      if (pog.Selected === singleValue.yes) {
        pog = {
          ...pog,
          ReleaseDate: null,
          ReleaseStatus: orderStatus.notReleased,
        };
      }
      updatedPogs.push(pog);
    }
  }
  return updatedPogs;
};

export const pogOSOAutoSelectUpdate = (pogs = [], updatedPogs = []) => {
  const _pogs = [];
  for (let i = 0, len = pogs.length; i < len; i++) {
    const pog = pogs[i];
    const _pog =
      updatedPogs?.find((_p) => _p?.PlanogramNumber === pog.PlanogramNumber) ||
      null;
    if (_pog) {
      _pogs.push({
        ...pog,
        ..._pog,
        __original: {
          ..._pog,
          Selected: _pog?.Selected,
          selection: false,
          virtualLoading: true,
        },
      });
    }
    _pogs.push(pog);
  }
  return _pogs;
};
